import { useState, useEffect, useMemo, useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import { listSheets, deleteSheet, bulkDeleteSheets, deserializeSheet, getSheet } from '@/lib/store-sheet';
import { JEWELRY_TYPES } from '@/utils/cad-sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Trash2, Pencil, Eye, Search, X, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { LivePreview } from './LivePreview';
import { CADSheetBody, DEFAULT_VISIBILITY } from './CADSheetBody';
import { toPng } from 'html-to-image';
import JSZip from 'jszip';

export function AllSheetsPanel({ onEditSheet, refreshKey }) {
  const [sheets, setSheets] = useState([]);
  const [styleSearch, setStyleSearch] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [jewelryFilter, setJewelryFilter] = useState('all');
  const [selected, setSelected] = useState(new Set());
  const [previewData, setPreviewData] = useState(null);
  const [bulkExporting, setBulkExporting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showCompanyFooter, setShowCompanyFooter] = useState(true);

  const loadSheets = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listSheets();
      setSheets(data);
    } catch { setSheets([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    loadSheets();
    setSelected(new Set());
  }, [refreshKey, loadSheets]);

  const filtered = useMemo(() => {
    let result = sheets;
    if (jewelryFilter !== 'all') result = result.filter(s => s.jewelryType === jewelryFilter);
    if (styleSearch.trim()) { const q = styleSearch.toLowerCase(); result = result.filter(s => s.styleNo.toLowerCase().includes(q)); }
    if (customerSearch.trim()) { const q = customerSearch.toLowerCase(); result = result.filter(s => (s.customerName || '').toLowerCase().includes(q)); }
    return result;
  }, [sheets, styleSearch, customerSearch, jewelryFilter]);

  const hasActiveFilters = jewelryFilter !== 'all' || styleSearch.trim() || customerSearch.trim();

  const toggleSelect = (id) => {
    setSelected(prev => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  };
  const toggleSelectAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map(s => s.id)));
  };

  const handleBulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} sheet(s) permanently?`)) return;
    const count = selected.size;
    await bulkDeleteSheets([...selected]);
    await loadSheets();
    setSelected(new Set());
    toast.success(`${count} sheet(s) deleted`);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this sheet permanently?')) return;
    await deleteSheet(id);
    await loadSheets();
    setSelected(prev => { const n = new Set(prev); n.delete(id); return n; });
    toast.success('Sheet deleted');
  };

  const handlePreview = async (id) => {
    const stored = await getSheet(id);
    if (stored) {
      const data = deserializeSheet(stored);
      // Stash the linked task id so LivePreview's "Push to SGI" knows which order to push to.
      setPreviewData({ data, taskId: data._linkedTaskId || null });
    }
  };

  const handleEdit = async (id) => {
    const stored = await getSheet(id);
    if (stored) {
      const data = deserializeSheet(stored);
      // Forward linked_task_id so the editor restores the task linkage on reopen.
      onEditSheet(data, id, data._linkedTaskId || null);
      toast.success('Sheet loaded for editing');
    }
  };

  const handleBulkDownload = async () => {
    if (selected.size === 0) return;
    setBulkExporting(true);
    toast.info(`Exporting ${selected.size} sheet(s)...`);

    const usedNames = new Map();
    const uniqueName = (base) => {
      const safe = (base || 'CAD-Sheet').replace(/[^a-zA-Z0-9._-]/g, '_');
      const n = (usedNames.get(safe) || 0) + 1;
      usedNames.set(safe, n);
      return n === 1 ? safe : `${safe}_${n}`;
    };

    const zip = new JSZip();
    let succeeded = 0;
    let failed = 0;

    for (const id of selected) {
      const stored = await getSheet(id);
      if (!stored) { failed++; continue; }
      const sheetData = deserializeSheet(stored);

      const container = document.createElement('div');
      container.style.cssText = 'position:fixed;left:-9999px;top:0;';
      document.body.appendChild(container);
      const root = createRoot(container);

      try {
        await new Promise((resolve) => {
          root.render(
            <CADSheetBody
              data={sheetData}
              visibility={DEFAULT_VISIBILITY}
              showCompanyFooter={showCompanyFooter}
            />
          );
          // Wait one paint, then ensure all images load
          requestAnimationFrame(() => requestAnimationFrame(resolve));
        });

        const imgs = Array.from(container.querySelectorAll('img'));
        await Promise.all(imgs.map(img => {
          if (img.complete && img.naturalWidth > 0) return Promise.resolve();
          return new Promise((resolve) => {
            const done = () => resolve();
            img.addEventListener('load', done, { once: true });
            img.addEventListener('error', done, { once: true });
            // Safety timeout \u2014 never block the export indefinitely on a broken URL
            setTimeout(done, 5000);
          });
        }));

        const target = container.firstElementChild;
        if (!target) throw new Error('Sheet body did not render');
        const dataUrl = await toPng(target, { pixelRatio: 3, backgroundColor: '#ffffff' });
        const base = uniqueName(sheetData.styleNo || 'CAD-Sheet');
        zip.file(`${base}.png`, dataUrl.split(',')[1], { base64: true });
        succeeded++;
      } catch (err) {
        console.error('Bulk export failed for', id, err);
        failed++;
      } finally {
        root.unmount();
        document.body.removeChild(container);
      }
    }

    if (succeeded > 0) {
      const blob = await zip.generateAsync({ type: 'blob' });
      const link = document.createElement('a');
      link.download = `CAD-Sheets_${new Date().toISOString().split('T')[0]}.zip`;
      link.href = URL.createObjectURL(blob);
      link.click();
      URL.revokeObjectURL(link.href);
    }

    setBulkExporting(false);
    if (failed === 0) toast.success(`Exported ${succeeded} sheet(s)`);
    else if (succeeded === 0) toast.error('Bulk export failed');
    else toast.warning(`Exported ${succeeded}, failed ${failed}`);
  };

  return (
    <div className="flex flex-col h-full" data-testid="cad-all-sheets">
      <div className="p-4 border-b border-border space-y-2 bg-card/50">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input value={styleSearch} onChange={e => setStyleSearch(e.target.value)} placeholder="Search by Style No..." className="h-8 text-xs pl-8" />
          </div>
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input value={customerSearch} onChange={e => setCustomerSearch(e.target.value)} placeholder="Search by Customer..." className="h-8 text-xs pl-8" />
          </div>
          <Select value={jewelryFilter} onValueChange={setJewelryFilter}>
            <SelectTrigger className="h-8 text-xs w-[130px]"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {JEWELRY_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          {hasActiveFilters && (
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setStyleSearch(''); setCustomerSearch(''); setJewelryFilter('all'); }} title="Clear filters"><X className="h-3.5 w-3.5" /></Button>
          )}
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Checkbox checked={filtered.length > 0 && selected.size === filtered.length} onCheckedChange={toggleSelectAll} className="h-3.5 w-3.5" />
            <span className="text-xs text-muted-foreground">{selected.size > 0 ? `${selected.size} selected` : `${filtered.length} sheets`}</span>
          </div>
          {selected.size > 0 && (
            <div className="flex items-center gap-1.5">
              <Button variant="outline" size="sm" className="h-7 text-xs" onClick={handleBulkDownload} disabled={bulkExporting}>
                {bulkExporting ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Download className="h-3 w-3 mr-1" />}Download ZIP ({selected.size})
              </Button>
              <Button variant="destructive" size="sm" className="h-7 text-xs" onClick={handleBulkDelete}>
                <Trash2 className="h-3 w-3 mr-1" /> Delete ({selected.size})
              </Button>
            </div>
          )}
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer ml-auto" data-testid="footer-toggle">
            <Checkbox checked={showCompanyFooter} onCheckedChange={setShowCompanyFooter} className="h-3.5 w-3.5" />
            Show company footer
          </label>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4">
        {loading ? (
          <div className="text-center text-muted-foreground py-16"><Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" /><p className="text-sm">Loading sheets...</p></div>
        ) : filtered.length === 0 ? (
          <div className="text-center text-muted-foreground py-16"><p className="text-sm">{hasActiveFilters ? 'No matching sheets.' : 'No saved sheets yet.'}</p></div>
        ) : (
          <div className="space-y-2">
            {filtered.map(sheet => (
              <div key={sheet.id} className={`border rounded-lg p-3 transition-colors ${selected.has(sheet.id) ? 'border-primary bg-primary/5' : 'border-border bg-card hover:bg-muted/30'}`}>
                <div className="flex items-start gap-3">
                  <Checkbox checked={selected.has(sheet.id)} onCheckedChange={() => toggleSelect(sheet.id)} className="mt-0.5 h-4 w-4" />
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm text-foreground truncate">{sheet.styleNo || 'Untitled'}</div>
                    {sheet.customerName && (<div className="text-xs text-muted-foreground truncate">{sheet.customerName}{sheet.customerDesignNo && ` \u2022 ${sheet.customerDesignNo}`}</div>)}
                    <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                      <span className="px-1.5 py-0.5 rounded bg-muted text-[11px]">{sheet.jewelryType}</span>
                      <span>{sheet.metals.map(m => m.karat).join('/')}</span>
                      <span>{'\u2022'}</span>
                      <span>{new Date(sheet.savedAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground" onClick={() => handlePreview(sheet.id)} title="Preview"><Eye className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-primary" onClick={() => handleEdit(sheet.id)} title="Edit"><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => handleDelete(sheet.id)} title="Delete"><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {previewData && (
        <LivePreview
          data={previewData.data}
          taskId={previewData.taskId}
          open={true}
          onClose={() => setPreviewData(null)}
        />
      )}
    </div>
  );
}
